import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import {
  Box, Typography, Paper, Stack, Button, IconButton, TextField, MenuItem, Chip, Alert,
  LinearProgress, Dialog, DialogContent, DialogTitle, Snackbar,
} from '@mui/material';
import {
  CloudUploadOutlined, DownloadOutlined, DeleteOutlineOutlined, InsertDriveFileOutlined,
  ContentPasteOutlined, CloseOutlined, VisibilityOutlined,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  DocumentCategoryLabels, type Document, type DocumentCategory,
} from '@lorrainemarwood-care/shared';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';

const CATEGORIES: (DocumentCategory | 'all')[] = ['all', 'medical', 'scan', 'manuscript', 'legal', 'photo', 'other'];

function isImage(mime: string): boolean {
  return mime.startsWith('image/');
}
function isPdf(mime: string): boolean {
  return mime === 'application/pdf';
}
function isPreviewable(mime: string): boolean {
  return isImage(mime) || isPdf(mime);
}

export default function Documents() {
  const { user } = useAuth();
  const [filter, setFilter] = useState<DocumentCategory | 'all'>('all');
  const [category, setCategory] = useState<DocumentCategory>('medical');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<Document | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const listQuery = useQuery({
    queryKey: ['documents', { filter }],
    queryFn: () =>
      api.get<Document[]>(filter === 'all' ? '/api/documents' : `/api/documents?category=${filter}`),
  });

  const uploadMutation = useMutation({
    mutationFn: async (input: { file: File; category: DocumentCategory; title?: string; description?: string }) => {
      const fd = new FormData();
      fd.append('file', input.file);
      fd.append('category', input.category);
      if (input.title) fd.append('title', input.title);
      if (input.description) fd.append('description', input.description);
      return api.upload<Document>('/api/documents', fd);
    },
    onSuccess: (data) => {
      setFile(null);
      setTitle('');
      setDescription('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      qc.invalidateQueries({ queryKey: ['documents'] });
      setToast(`Uploaded "${data.title ?? data.filename}"`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/documents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    if (f && !title) setTitle(f.name.replace(/\.[^/.]+$/, ''));
  };

  // ── Paste handler ────────────────────────────────────────
  // Any paste event that contains a file (screenshot, image, copied PDF) on this
  // page immediately uploads with the current category. Title is derived from
  // the pasted filename or a timestamp for clipboard screenshots.
  useEffect(() => {
    const onPaste = (e: globalThis.ClipboardEvent) => {
      // Don't hijack pastes inside text inputs — those belong to the inputs.
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      const items = Array.from(e.clipboardData?.items ?? []);
      const fileItems = items.filter((i) => i.kind === 'file');
      if (fileItems.length === 0) return;
      e.preventDefault();
      for (const item of fileItems) {
        const pasted = item.getAsFile();
        if (!pasted) continue;
        // Browsers often name clipboard screenshots "image.png" — make that
        // distinct per paste so the user can tell them apart.
        const stamp = dayjs().format('YYYYMMDD-HHmmss');
        const filename = pasted.name && pasted.name !== 'image.png'
          ? pasted.name
          : `paste-${stamp}.${(pasted.type.split('/')[1] ?? 'png').replace('jpeg', 'jpg')}`;
        const withName = new File([pasted], filename, { type: pasted.type });
        uploadMutation.mutate({
          file: withName,
          category,
          title: filename.replace(/\.[^/.]+$/, ''),
        });
      }
    };
    window.addEventListener('paste', onPaste as EventListener);
    return () => window.removeEventListener('paste', onPaste as EventListener);
  }, [category, uploadMutation]);

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>Documents</Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Upload</Typography>
          <Chip
            icon={<ContentPasteOutlined />}
            label="Paste anywhere on this page to upload"
            size="small"
            variant="outlined"
            sx={{ ml: 'auto' }}
          />
        </Stack>
        <Stack spacing={2}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUploadOutlined />}
            sx={{ alignSelf: 'flex-start' }}
          >
            {file ? file.name : 'Choose file'}
            <input ref={fileInputRef} type="file" hidden onChange={onFile} />
          </Button>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="Category"
              size="small"
              value={category}
              onChange={(e) => setCategory(e.target.value as DocumentCategory)}
              sx={{ minWidth: 180 }}
            >
              {(Object.keys(DocumentCategoryLabels) as DocumentCategory[]).map((c) => (
                <MenuItem key={c} value={c}>{DocumentCategoryLabels[c]}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Title (optional)"
              size="small"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
            />
          </Stack>
          <TextField
            label="Description (optional)"
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
          {uploadMutation.isPending && <LinearProgress />}
          {uploadMutation.isError && <Alert severity="error">{(uploadMutation.error as Error).message}</Alert>}
          <Box>
            <Button
              variant="contained"
              onClick={() => file && uploadMutation.mutate({ file, category, title, description })}
              disabled={!file || uploadMutation.isPending}
            >
              {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }}>
        {CATEGORIES.map((c) => (
          <Chip
            key={c}
            label={c === 'all' ? 'All' : DocumentCategoryLabels[c]}
            onClick={() => setFilter(c)}
            variant={filter === c ? 'filled' : 'outlined'}
            color={filter === c ? 'primary' : 'default'}
          />
        ))}
      </Stack>

      <Stack spacing={1.5}>
        {(listQuery.data ?? []).map((d) => {
          const canDelete = user?.id === d.uploadedById || user?.role === 'admin';
          const previewable = isPreviewable(d.mimeType);
          const downloadUrl = `/api/documents/${d.id}/download`;
          return (
            <Paper key={d.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                {isImage(d.mimeType) ? (
                  <Box
                    component="img"
                    src={downloadUrl}
                    alt=""
                    onClick={() => setPreviewing(d)}
                    sx={{
                      width: 56, height: 56, borderRadius: 1, objectFit: 'cover',
                      cursor: 'pointer', bgcolor: 'action.hover',
                    }}
                  />
                ) : (
                  <InsertDriveFileOutlined color="action" />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    noWrap
                    onClick={previewable ? () => setPreviewing(d) : undefined}
                    sx={previewable ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : undefined}
                  >
                    {d.title ?? d.filename}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                    <Chip size="small" label={DocumentCategoryLabels[d.category]} variant="outlined" />
                    <Typography variant="caption" color="text.secondary">
                      {d.uploadedByName} · {dayjs(d.createdAt).format('DD MMM YYYY')} · {formatSize(d.size)} · {d.mimeType.split('/')[1] ?? d.mimeType}
                    </Typography>
                  </Stack>
                  {d.description && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{d.description}</Typography>
                  )}
                </Box>
                {previewable && (
                  <IconButton onClick={() => setPreviewing(d)}>
                    <VisibilityOutlined />
                  </IconButton>
                )}
                <IconButton
                  component="a"
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener"
                >
                  <DownloadOutlined />
                </IconButton>
                {canDelete && (
                  <IconButton onClick={() => deleteMutation.mutate(d.id)} disabled={deleteMutation.isPending}>
                    <DeleteOutlineOutlined />
                  </IconButton>
                )}
              </Stack>
            </Paper>
          );
        })}
        {(listQuery.data ?? []).length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No documents yet.</Box>
        )}
      </Stack>

      <PreviewDialog doc={previewing} onClose={() => setPreviewing(null)} />

      <Snackbar
        open={toast !== null}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast}
      />
    </Box>
  );
}

function PreviewDialog({ doc, onClose }: { doc: Document | null; onClose: () => void }) {
  if (!doc) return null;
  const src = `/api/documents/${doc.id}/download`;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap>{doc.title ?? doc.filename}</Typography>
          <Typography variant="caption" color="text.secondary">
            {doc.filename} · {doc.mimeType}
          </Typography>
        </Box>
        <IconButton href={src} target="_blank" rel="noopener">
          <DownloadOutlined />
        </IconButton>
        <IconButton onClick={onClose}>
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: 'background.default' }}>
        {isImage(doc.mimeType) && (
          <Box
            component="img"
            src={src}
            alt=""
            sx={{ display: 'block', width: '100%', height: 'auto', maxHeight: '80vh', objectFit: 'contain' }}
          />
        )}
        {isPdf(doc.mimeType) && (
          <Box
            component="iframe"
            src={src}
            sx={{ width: '100%', height: '80vh', border: 'none' }}
            title={doc.filename}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
